'use client';

import React, { useState, useTransition } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import useNavigation from '@/lib/hooks/useNavigation';
import { BranchIndustries, IBranchDetails, SubIndustry } from '@/types/stores';
import { filterContainerAnimation, filterItemAnimation } from '@/components/animation';
import { trackEvent } from '@/lib/amplitude';
import { AmplitudeEvents } from '@/lib/amplitude/events';
import CurrentLanguageText from '@/components/helper/CurrentLanguageText';
import Spinner from '@/components/ui/Spinner';

type AccordionProps = {
  category: BranchIndustries;
  details: IBranchDetails;
  expanded: string | false;
  setExpanded: (expanded: string | false) => void;
};

const SubCategory = ({
  subcategories,
  categoryName,
  details,
}: {
  subcategories: SubIndustry[] | null;
  categoryName: string;
  details: IBranchDetails;
}) => {
  const { createQueryString, removeQueryString } = useNavigation();
  const [isPending, startTransition] = useTransition();
  const params = useSearchParams();
  const subCategory = params.get('subcategory');

  const handleSelect = (subcategory: SubIndustry) => {
    const isSelected = subcategory.englishName === subCategory;
    const query: any = isSelected
      ? ['category', 'subcategory']
      : { category: categoryName, subcategory: subcategory.englishName };

    startTransition(() => {
      isSelected ? removeQueryString(query) : createQueryString(query);
    });

    trackEvent(AmplitudeEvents.Store_SubIndustry_Clicked, {
      storeID: details.store.id,
      storeName: details.store.englishName,
      branchID: details.id,
      branchName: details.englishName,
      subIndustryID: subcategory.id,
      subIndustryName: subcategory.englishName,
    });
  };

  return (
    <div className="bg-primary/10 me-1 grid cursor-pointer auto-cols-max grid-flow-col gap-3 rounded-2xl px-7 py-3 text-sm">
      {subcategories?.map((subcategory, index) => {
        const isSelected = subcategory.englishName === subCategory;

        return (
          <motion.button
            key={index}
            variants={filterItemAnimation}
            className={`${
              isSelected ? 'text-primary font-extrabold' : 'text-gray-2'
            } item-center loa both centerButton flex cursor-pointer gap-1`}
            disabled={isPending}
            onClick={() => handleSelect(subcategory)}
          >
            <CurrentLanguageText
              arabicName={subcategory.arabicName}
              englishName={subcategory.englishName}
            />
            {isPending ? (
              <Spinner size="small" />
            ) : (
              isSelected && <X size={15} strokeWidth={4} className="mt-0.5" />
            )}
          </motion.button>
        );
      })}
    </div>
  );
};

const Accordion = ({ category, details, expanded, setExpanded }: AccordionProps) => {
  const { removeQueryString } = useNavigation();
  const [isPending, startTransition] = useTransition();
  const params = useSearchParams();
  const categoryParams = params.get('category');
  const subCategoryParams = params.get('subcategory');
  const isOpen = category.englishName === expanded || category.englishName === categoryParams;

  const handleToggleCollapsed = () => {
    const isCurrentOpen = isOpen;

    startTransition(() => {
      setExpanded(isCurrentOpen ? false : category.englishName); // Ensure correct type here
      if (isCurrentOpen && categoryParams && subCategoryParams) {
        removeQueryString(['category', 'subcategory']);
      }
    });

    trackEvent(AmplitudeEvents.Store_Industry_Clicked, {
      storeID: details.store.id,
      storeName: details.store.englishName,
      branchID: details.id,
      branchName: details.englishName,
      industryID: category.id,
      industryName: category.englishName,
    });
  };

  return (
    <div className="grid auto-cols-max grid-flow-col">
      <div className="my-2 grid auto-cols-max grid-flow-col items-center">
        <motion.button
          initial={false}
          disabled={isPending}
          animate={{
            backgroundColor: isOpen ? '#1a1a1a' : '#fff',
            color: isOpen ? '#fff' : '#1a1a1a',
            spacing: isOpen ? '0.2em' : '0.1em',
          }}
          onClick={handleToggleCollapsed}
          className="centerButton border-secondary me-2 grid cursor-pointer auto-cols-max grid-flow-col gap-2 overflow-hidden rounded-2xl border-2 px-8 py-3 text-sm font-medium"
        >
          <CurrentLanguageText
            arabicName={category.arabicName}
            englishName={category.englishName}
          />
          {isOpen && (
            <motion.span className="mt-0.5">
              <X size={15} strokeWidth={4} />
            </motion.span>
          )}
        </motion.button>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.section
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={filterContainerAnimation}
            >
              <SubCategory
                subcategories={category.subIndustries}
                categoryName={category.englishName}
                details={details}
              />
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const Filter = ({ list, details }: { list: BranchIndustries[]; details: IBranchDetails }) => {
  const [expanded, setExpanded] = useState<false | string>(false);

  return (
    <section className="styled-scroll mb-4 flex overflow-auto pt-5 pb-1">
      {Array.isArray(list) &&
        list?.map((category, index) => (
          <Accordion
            key={index}
            category={category}
            expanded={expanded}
            setExpanded={setExpanded}
            details={details}
          />
        ))}
    </section>
  );
};

export default Filter;
